import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Package, Plus, Edit, Trash2, Search, Filter, FileText, Settings } from 'lucide-react';
import { API_BASE_URL } from '../config';

const DEFAULT_FORM_DATA = {
  product_code: '',
  name: '',
  description: '',
  category: '',
  manufacturer_name: '',
  manufacturer_part_number: '',
  image_url: '',
  status: 'active'
};

const Products = () => {
  const { getAuthHeaders } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_FORM_DATA });
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDocuments, setProductDocuments] = useState([]);
  const [allDocuments, setAllDocuments] = useState([]); // kept for compatibility, no longer used for listing
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  // Document selector pagination/search state
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentPage, setDocumentPage] = useState(1);
  const [documentPageSize, setDocumentPageSize] = useState(20);
  const [documentTotalCount, setDocumentTotalCount] = useState(0);
  const [documentHasNext, setDocumentHasNext] = useState(false);
  const [selectorDocuments, setSelectorDocuments] = useState([]);
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmTitle, setConfirmTitle] = useState('Confirm Action');
  const [confirmDetails, setConfirmDetails] = useState(null); // { rows: [[label,value], ...], note?: string }
  // Pagination state
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasDocuments, setHasDocuments] = useState(''); // '', 'with', 'without'

  useEffect(() => {
    fetchProducts();
  }, [page, pageSize, searchTerm, hasDocuments]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      if (!headers) return; // Exit if not authenticated
      
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String((page - 1) * pageSize),
        search: searchTerm || ''
      });
      if (hasDocuments) params.set('has_documents', hasDocuments);
      const response = await fetch(`${API_BASE_URL}/api/v1/products/?${params.toString()}`, {
        headers: headers
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
        const p = data.pagination || {};
        setTotalCount(Number(p.total_count || 0));
        setHasNext(Boolean(p.has_next));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingProduct 
        ? `${API_BASE_URL}/api/v1/products/${encodeURIComponent(editingProduct.product_code)}`
        : `${API_BASE_URL}/api/v1/products/`;
      
      const method = editingProduct ? 'PUT' : 'POST';
      
      const headers = getAuthHeaders();
      if (!headers) return; // Exit if not authenticated
      
      const response = await fetch(url, {
        method,
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowCreateModal(false);
        setEditingProduct(null);
        setFormData({
          product_code: '',
          name: '',
          description: '',
          category: '',
          status: 'active'
        });
        fetchProducts();
      }
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      product_code: product.product_code,
      name: product.name,
      description: product.description,
      category: product.category,
      manufacturer_name: product.manufacturer_name || '',
      manufacturer_part_number: product.manufacturer_part_number || '',
      image_url: product.image_url || '',
      status: product.status
    });
    setShowCreateModal(true);
  };

  const handleManageDocuments = async (product) => {
    console.log('🔍 Managing documents for product:', product);
    console.log('🔍 Product code:', product?.product_code);
    setSelectedProduct(product);
    setShowDocumentsModal(true);
    // Reset selector filters
    setDocumentSearch('');
    setDocumentPage(1);
    await loadProductDocuments(product.product_code);
    await loadSelectorDocuments(product.product_code);
  };

  const showConfirmation = (message, action, options = {}) => {
    setConfirmTitle(options.title || 'Confirm Action');
    setConfirmMessage(message);
    setConfirmDetails(options.details || null);
    setConfirmAction(() => action);
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    if (confirmAction) {
      await confirmAction();
    }
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmMessage('');
  };

  const handleUnlinkDocumentFromProduct = async (docId) => {
    if (!selectedProduct) return;
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const newIds = selectedDocuments.filter(id => id !== docId);
      const resp = await fetch(`${API_BASE_URL}/api/v1/products/${encodeURIComponent(selectedProduct.product_code)}/documents`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_ids: newIds })
      });
      if (resp.ok) {
        setSelectedDocuments(newIds);
        setProductDocuments(prev => prev.filter(d => d.id !== docId));
        await loadSelectorDocuments(selectedProduct.product_code);
      } else {
        const err = await resp.json().catch(()=>({detail:'Unknown error'}));
        alert(`Unlink failed: ${err.detail || 'Unknown error'}`);
      }
    } catch (e) {
      console.error('Error unlinking document:', e);
      alert('Failed to unlink document.');
    }
  };

  const loadProductDocuments = async (productCode) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const productResponse = await fetch(`${API_BASE_URL}/api/v1/products/${encodeURIComponent(productCode)}/documents`, {
        headers: headers
      });
      if (productResponse.ok) {
        const productData = await productResponse.json();
        setProductDocuments(productData.documents || []);
        setSelectedDocuments((productData.documents || []).map(doc => doc.id));
      }
    } catch (error) {
      console.error('Error fetching product documents:', error);
      setProductDocuments([]);
      setSelectedDocuments([]);
    }
  };

  const loadSelectorDocuments = async (productCode) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const params = new URLSearchParams({
        limit: String(documentPageSize),
        offset: String((documentPage - 1) * documentPageSize),
        search: documentSearch || ''
      });
      const resp = await fetch(`${API_BASE_URL}/api/v1/products/${encodeURIComponent(productCode)}/documents/selector?${params.toString()}`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        setSelectorDocuments(data.documents || []);
        const p = data.pagination || {};
        setDocumentTotalCount(Number(p.total_count || 0));
        setDocumentHasNext(Boolean(p.has_next));
      }
    } catch (error) {
      console.error('Error fetching selector documents:', error);
      setSelectorDocuments([]);
      setDocumentTotalCount(0);
      setDocumentHasNext(false);
    }
  };

  useEffect(() => {
    if (showDocumentsModal && selectedProduct) {
      loadSelectorDocuments(selectedProduct.product_code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentPage, documentPageSize, documentSearch]);

  const handleUpdateProductDocuments = async () => {
    if (!selectedProduct) return;

    console.log('🚀 Updating documents for selectedProduct:', selectedProduct);
    console.log('🚀 Selected product code:', selectedProduct?.product_code);
    console.log('🚀 API URL will be:', `${API_BASE_URL}/api/v1/products/${encodeURIComponent(selectedProduct.product_code)}/documents`);

    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      
      const response = await fetch(`${API_BASE_URL}/api/v1/products/${encodeURIComponent(selectedProduct.product_code)}/documents`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          document_ids: selectedDocuments
        })
      });

      if (response.ok) {
        setShowDocumentsModal(false);
        setSelectedProduct(null);
        setProductDocuments([]);
        setAllDocuments([]);
        setSelectedDocuments([]);
        fetchProducts();
        alert('Product documents updated successfully!');
      } else {
        const errorData = await response.json();
        console.error('❌ API Error Response:', errorData);
        console.error('❌ Response status:', response.status);
        alert(`Update failed: ${response.status} - ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Network/Fetch Error:', error);
      alert('Update failed: ' + error.message);
    }
  };

  const handleDelete = async (productCode) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        const headers = getAuthHeaders();
        if (!headers) return; // Exit if not authenticated
        
        const response = await fetch(`${API_BASE_URL}/api/v1/products/${encodeURIComponent(productCode)}`, {
          method: 'DELETE',
          headers: headers
        });

        if (response.ok) {
          // Remove the product from the local state immediately for better UX
          setProducts(prevProducts => 
            prevProducts.filter(product => product.product_code !== productCode)
          );
          // Also fetch fresh data to ensure consistency
          fetchProducts();
        } else if (response.status === 404) {
          // Product already deleted, just refresh the list
          console.log('Product already deleted, refreshing list');
          fetchProducts();
        } else {
          throw new Error(`Delete failed with status: ${response.status}`);
        }
      } catch (error) {
        console.error('Error deleting product:', error);
        // Refresh the list in case of any error to ensure UI is in sync
        fetchProducts();
      }
    }
  };

  // Reset to first page when filters or page size change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize]);

  const totalPages = Math.max(1, Math.ceil(totalCount / Math.max(1, pageSize)));
  const startIdx = (page - 1) * pageSize;
  const endIdx = startIdx + (products?.length || 0);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <h2>Products</h2>
          <p className="text-muted">Manage your product catalog</p>
        </div>
        <div className="col-auto">
          <button
            className="btn btn-primary"
            onClick={() => {
              setEditingProduct(null);
              setFormData({ ...DEFAULT_FORM_DATA });
              setShowCreateModal(true);
            }}
          >
            <Plus size={20} className="me-2" />
            Add Product
          </button>
          <div className="btn-group ms-2">
            <button className="btn btn-outline-secondary" onClick={async ()=>{
              const headers = getAuthHeaders();
              const res = await fetch(`${API_BASE_URL}/api/v1/export/products?format=csv`, { headers });
              const blob = await res.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `products_export_${Date.now()}.csv`; document.body.appendChild(a); a.click(); a.remove();
              window.URL.revokeObjectURL(url);
            }}>Export CSV</button>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text">
              <Search size={20} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="d-flex gap-2">
            <div className="input-group">
              <span className="input-group-text"><Filter size={20} /></span>
              <select
                className="form-select"
                value={hasDocuments}
                onChange={(e)=>setHasDocuments(e.target.value)}
              >
                <option value="">All products</option>
                <option value="with">With documents</option>
                <option value="without">Without documents</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="row">
        <div className="col">
          <div className="card">
            <div className="card-body">
              {products.length === 0 ? (
                <div className="text-center py-5">
                  <Package size={64} className="text-muted mb-3" />
                  <h5>No products found</h5>
                  <p className="text-muted">Create your first product to get started</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setEditingProduct(null);
                      setFormData({ ...DEFAULT_FORM_DATA });
                      setShowCreateModal(true);
                    }}
                  >
                    <Plus size={20} className="me-2" />
                    Add Product
                  </button>
                </div>
              ) : (
                <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <label className="form-label mb-0">Per page:</label>
                    <select className="form-select form-select-sm" style={{ width: 90 }} value={pageSize} onChange={(e)=>setPageSize(Number(e.target.value))}>
                      {[10,20,30].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="small text-muted">Showing {products.length === 0 ? 0 : (startIdx + 1)}–{endIdx} of {totalCount}</div>
                  <div className="btn-group btn-group-sm" role="group">
                    <button className="btn btn-outline-secondary" disabled={page<=1} onClick={()=>setPage(p=>Math.max(1, p-1))}>Prev</button>
                    <button className="btn btn-outline-secondary" disabled={!hasNext} onClick={()=>setPage(p=>p+1)}>Next</button>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th style={{ width: 120 }}>Image</th>
                        <th>Product</th>
                        <th>Manufacturer</th>
                        <th>Category</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product) => (
                        <tr key={product.product_code}>
                          <td>
                            <div className="border rounded d-flex align-items-center justify-content-center bg-light" style={{ width: 100, height: 100, overflow: 'hidden' }}>
                              {product.image_url ? (
                                <img
                                  src={product.image_url}
                                  alt={product.name || product.product_code}
                                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = 'https://via.placeholder.com/100?text=No+Image';
                                  }}
                                />
                              ) : (
                                <span className="text-muted small text-center px-2">No image</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex flex-column">
                              <code className="bg-light px-2 py-1 rounded d-inline-block mb-1">{product.product_code}</code>
                              <div className="fw-bold">{product.name}</div>
                              {product.description && (
                                <small className="text-muted">{product.description}</small>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex flex-column">
                              <span className="fw-semibold">{product.manufacturer_name || '—'}</span>
                              <small className="text-muted">
                                Part #: {product.manufacturer_part_number || '—'}
                              </small>
                            </div>
                          </td>
                          <td>
                            <span className="badge bg-info">
                              {product.category || 'Uncategorized'}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${product.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                              {product.status}
                            </span>
                          </td>
                          <td>{product.created_at ? new Date(product.created_at).toLocaleDateString() : 'N/A'}</td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-primary"
                                title="Edit Product"
                                onClick={() => handleEdit(product)}
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                className="btn btn-outline-info"
                                title="Manage Documents"
                                onClick={() => handleManageDocuments(product)}
                              >
                                <Settings size={16} />
                              </button>
                              <button
                                className="btn btn-outline-danger"
                                title="Delete Product"
                                onClick={() => handleDelete(product.product_code)}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="small text-muted">Page {page} of {totalPages}</div>
                    <div className="btn-group btn-group-sm" role="group">
                      <button className="btn btn-outline-secondary" disabled={page<=1} onClick={()=>setPage(p=>Math.max(1, p-1))}>Prev</button>
                      <button className="btn btn-outline-secondary" disabled={!hasNext} onClick={()=>setPage(p=>p+1)}>Next</button>
                    </div>
                  </div>
                </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingProduct ? 'Edit Product' : 'Create Product'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCreateModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Product Code *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.product_code}
                        onChange={(e) => setFormData({...formData, product_code: e.target.value})}
                        required
                        disabled={!!editingProduct}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Category</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Manufacturer Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.manufacturer_name}
                        onChange={(e) => setFormData({ ...formData, manufacturer_name: e.target.value })}
                        placeholder="e.g., Geary Pacific"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Manufacturer Part #</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.manufacturer_part_number}
                        onChange={(e) => setFormData({ ...formData, manufacturer_part_number: e.target.value })}
                        placeholder="e.g., ABC-12345"
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Image URL</label>
                    <input
                      type="url"
                      className="form-control"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="https://example.com/product.jpg"
                    />
                    <div className="form-text">Used for the thumbnail shown in the product list (100 x 100).</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingProduct ? 'Update' : 'Create'} Product
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Document Management Modal */}
      {showDocumentsModal && selectedProduct && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Manage Documents for: {selectedProduct.product_code} - {selectedProduct.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDocumentsModal(false);
                    setSelectedProduct(null);
                    setProductDocuments([]);
                    setAllDocuments([]);
                    setSelectedDocuments([]);
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Currently Associated Documents</label>
                  <div className="border rounded p-3" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {productDocuments.length === 0 ? (
                      <p className="text-muted">No documents currently associated</p>
                    ) : (
                      productDocuments.map((doc) => (
                        <span key={doc.id} className="badge bg-info me-2 mb-2 d-inline-flex align-items-center">
                          <FileText size={12} className="me-1" />
                          <span>{doc.original_filename} ({doc.file_type})</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-light ms-2"
                            title="Unlink document"
                            onClick={() => showConfirmation(
                              'Are you sure you want to unlink this document from the product?',
                              () => handleUnlinkDocumentFromProduct(doc.id),
                              {
                                title: 'Unlink Document from Product',
                                details: {
                                  rows: [
                                    ['Product', `${selectedProduct?.product_code || ''} — ${selectedProduct?.name || ''}`.trim()],
                                    ['Document', `${doc.original_filename} (${doc.file_type})`]
                                  ],
                                  note: 'This removes the association only. The document and product remain available.'
                                }
                              }
                            )}
                          >
                            Unlink
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Select Documents to Associate</label>
                  <div className="row g-2 align-items-center mb-2">
                    <div className="col-auto"><small className="text-muted">Search</small></div>
                    <div className="col"><input className="form-control form-control-sm" value={documentSearch} onChange={(e)=>{setDocumentSearch(e.target.value); setDocumentPage(1);}} placeholder="Search filename, description or type" /></div>
                    <div className="col-auto"><small className="text-muted">Per page</small></div>
                    <div className="col-auto">
                      <select className="form-select form-select-sm" value={documentPageSize} onChange={(e)=>{setDocumentPageSize(Number(e.target.value)); setDocumentPage(1);}}>
                        {[10,20,30].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="border rounded p-3" style={{ maxHeight: '260px', overflowY: 'auto' }}>
                    {selectorDocuments.length === 0 ? (
                      <p className="text-muted">No documents found</p>
                    ) : (
                      selectorDocuments.map((doc) => (
                        <div key={doc.id} className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`modal-doc-${doc.id}`}
                            checked={selectedDocuments.includes(doc.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDocuments(prev => [...prev, doc.id]);
                              } else {
                                setSelectedDocuments(prev => prev.filter(id => id !== doc.id));
                              }
                            }}
                          />
                          <label className="form-check-label" htmlFor={`modal-doc-${doc.id}`}>
                            <strong>{doc.original_filename || doc.filename}</strong>
                            <small className="text-muted d-block">
                              Type: {doc.file_type} | Size: {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : 'N/A'} {doc.is_associated ? '• Associated' : ''}
                            </small>
                            {doc.description && (
                              <small className="text-muted d-block">{doc.description}</small>
                            )}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="small text-muted">Page {documentPage} of {Math.max(1, Math.ceil(documentTotalCount / Math.max(1, documentPageSize)))}</div>
                    <div className="btn-group btn-group-sm" role="group">
                      <button className="btn btn-outline-secondary" disabled={documentPage<=1} onClick={()=>setDocumentPage(p=>Math.max(1, p-1))}>Prev</button>
                      <button className="btn btn-outline-secondary" disabled={!documentHasNext} onClick={()=>setDocumentPage(p=>p+1)}>Next</button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDocumentsModal(false);
                    setSelectedProduct(null);
                    setProductDocuments([]);
                    setAllDocuments([]);
                    setSelectedDocuments([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdateProductDocuments}
                >
                  Update Documents
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{confirmTitle}</h5>
                <button type="button" className="btn-close" onClick={() => setShowConfirmModal(false)} />
              </div>
              <div className="modal-body">
                <p className="mb-3">{confirmMessage}</p>
                {confirmDetails?.rows && (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-2">
                      <tbody>
                        {confirmDetails.rows.map(([label, value], idx) => (
                          <tr key={idx}>
                            <th style={{width: '140px'}} className="text-muted">{label}</th>
                            <td>{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {confirmDetails?.note && (
                  <div className="alert alert-warning py-2 mb-0">
                    {confirmDetails.note}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowConfirmModal(false)}>Cancel</button>
                <button type="button" className="btn btn-warning" onClick={handleConfirmAction}>Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
